import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Form, 
  FormControl, 
  FormField, 
  FormItem, 
  FormLabel 
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useForm } from "react-hook-form";
import { Check, Clock, Plus, X } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TimeController } from "./TimeController";

interface MedicationReminderConfigProps {
  medication: {
    id: string;
    name: string;
    dosage: string;
    reminder_time?: string[];
    active?: boolean;
  } | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface ReminderFormValues {
  active: boolean;
  reminder_time: string[];
}

export const MedicationReminderConfig = ({ medication, open, onOpenChange }: MedicationReminderConfigProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [newTime, setNewTime] = useState("");
  
  const form = useForm<ReminderFormValues>({
    defaultValues: {
      active: medication?.active || true,
      reminder_time: medication?.reminder_time || [],
    },
  });

  const watchActive = form.watch("active");
  const watchReminderTime = form.watch("reminder_time");

  const handleAddTime = () => {
    if (!newTime) return;
    
    if (!/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(newTime)) {
      toast({
        variant: "destructive",
        title: "Format invalide",
        description: "Le format de l'heure doit être HH:MM",
      });
      return;
    }

    if (watchReminderTime.includes(newTime)) {
      toast({
        variant: "destructive",
        title: "Heure existante",
        description: "Cette heure existe déjà dans la liste des rappels",
      });
      return;
    }

    const newTimes = [...watchReminderTime, newTime].sort();
    form.setValue("reminder_time", newTimes);
    setNewTime("");
  };

  const handleRemoveTime = (time: string) => {
    const newTimes = watchReminderTime.filter(t => t !== time);
    form.setValue("reminder_time", newTimes);
  };

  const onSubmit = async (values: ReminderFormValues) => {
    if (!medication) return;
    
    setIsLoading(true);
    try {
      const { error } = await supabase
        .from("medications")
        .update({
          active: values.active,
          reminder_time: values.reminder_time,
        })
        .eq("id", medication.id);

      if (error) throw error;

      toast({
        title: "Rappels configurés",
        description: "Les rappels ont été configurés avec succès",
      });
      
      queryClient.invalidateQueries({ queryKey: ["medications"] });
      onOpenChange(false);
    } catch (error) {
      console.error("Error in onSubmit:", error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de configurer les rappels",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <Clock className="mr-2 h-5 w-5" /> 
            Configurer les rappels
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[70vh] pr-4">
          {medication ? (
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <div className="space-y-2">
                  <h3 className="text-base font-medium">{medication.name}</h3>
                  <p className="text-sm text-muted-foreground">{medication.dosage}</p>
                </div>

                <FormField
                  control={form.control}
                  name="active"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <FormLabel className="text-base">Activer les rappels</FormLabel>
                        <p className="text-sm text-muted-foreground">
                          Recevez des notifications pour vous rappeler de prendre ce médicament
                        </p>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                {watchActive && (
                  <div className="space-y-4">
                    <TimeController 
                      reminderTimes={watchReminderTime} 
                      currentTime={new Date().toLocaleTimeString('fr-FR', { 
                        hour: '2-digit', 
                        minute: '2-digit',
                        hour12: false 
                      })}
                    />

                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base">Heures de rappel</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <Input
                              placeholder="HH:MM"
                              value={newTime}
                              onChange={(e) => setNewTime(e.target.value)}
                              className="w-32"
                            />
                            <Button 
                              type="button" 
                              size="sm" 
                              variant="outline"
                              onClick={handleAddTime}
                            >
                              <Plus className="h-4 w-4 mr-1" /> Ajouter
                            </Button>
                          </div>

                          {watchReminderTime.length === 0 ? (
                            <p className="text-sm text-muted-foreground italic py-2">
                              Aucune heure de rappel configurée
                            </p>
                          ) : (
                            <div className="mt-4 space-y-2">
                              {watchReminderTime.map((time, index) => (
                                <div 
                                  key={index} 
                                  className="flex items-center justify-between bg-secondary/20 p-2 rounded-md"
                                >
                                  <div className="flex items-center">
                                    <Clock className="h-4 w-4 mr-2 text-muted-foreground" />
                                    <span>{time}</span>
                                  </div>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleRemoveTime(time)}
                                    className="h-8 w-8 p-0"
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>

                    <div className="bg-blue-50 p-4 rounded-md">
                      <p className="text-sm text-blue-700 flex items-start">
                        <Check className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
                        Vous recevrez des notifications à ces heures pour vous rappeler de prendre votre médicament.
                      </p>
                    </div>
                  </div>
                )}

                <div className="flex justify-end space-x-2 pt-4">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => onOpenChange(false)}
                  >
                    Annuler
                  </Button>
                  <Button type="submit" disabled={isLoading}>
                    {isLoading ? "Enregistrement..." : "Enregistrer"}
                  </Button>
                </div>
              </form>
            </Form>
          ) : (
            <div className="py-4 text-center text-muted-foreground">
              Erreur de chargement du médicament
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
